/**
 * LongPostList Component
 * 
 * Main interface for browsing and managing long posts. This component provides:
 * - Grid view of all long posts with cards
 * - Category filtering functionality  
 * - Pagination support
 * - Create new post functionality
 * - Quick actions menu (edit metadata, delete)
 * - Navigation to chunk management for each post
 * 
 * Used in: App.js route /long-posts
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    Grid,
    TextField,
    MenuItem,
    Menu,
    MenuItem as MenuItemMui
} from '@mui/material';
import {
    Add,
    Edit,
    Delete
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import LongPostMetadataEditor from './LongPostMetadataEditor';
import LongPostCard from './LongPostCard';
import PageContainer from '../layout/PageContainer';

const LongPostList = () => {
    const navigate = useNavigate();
    const [longPosts, setLongPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPost, setCurrentPost] = useState(null);
    const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);

    const categories = [
        { value: 'all', label: 'All Categories' },
        { value: 'news', label: 'News' },
        { value: 'biography', label: 'Biography' },
        { value: 'tutorial', label: 'Tutorial' },
        { value: 'science', label: 'Science' },
        { value: 'academic', label: 'Academic' },
        { value: 'book', label: 'Book' },
        { value: 'other', label: 'Other' }
    ];

    const fetchLongPosts = async () => {
        setLoading(true);
        try {
            const response = await userRequest.get('/contentLongPost', {
                params: {
                    page,
                    limit: 10,
                    category: categoryFilter === 'all' ? undefined : categoryFilter
                }
            });

            setLongPosts(response.data.longPosts);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Error fetching long posts:', error);
            showAlert('Error fetching long posts: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLongPosts();
    }, [page, categoryFilter]);

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    };

    const handleCreatePost = () => {
        setCurrentPost(null);
        setMode('create');
    };

    const handleEditPost = (post) => {
        setCurrentPost(post);
        setMode('edit');
    };

    const handleCardClick = (post) => {
        navigate(`/long-posts/edit/${post._id}`);
    };

    const handleMenuClick = (event, post) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedPost(post);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPost(null);
    };

    const handleDeletePost = async (postId) => {
        handleMenuClose();
        if (window.confirm('Are you sure you want to delete this long post?')) {
            try {
                await userRequest.delete(`/contentLongPost/${postId}`);
                showAlert('Long post deleted successfully', 'success');
                fetchLongPosts();
            } catch (error) {
                showAlert('Error deleting long post: ' + error.message, 'error');
            }
        }
    };

    const handleSavePost = async (postData) => {
        try {
            if (mode === 'create') {
                await userRequest.post('/contentLongPost', postData);
                showAlert('Long post created successfully', 'success');
            } else if (mode === 'edit') {
                await userRequest.patch(`/contentLongPost/${currentPost._id}`, postData);
                showAlert('Long post updated successfully', 'success');
            }

            setMode('list');
            fetchLongPosts();
        } catch (error) {
            showAlert('Error saving long post: ' + error.message, 'error');
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo(0, 0);
    };

    if (mode === 'create' || mode === 'edit') {
        return (
            <PageContainer>
                <LongPostMetadataEditor
                    post={currentPost}
                    onSave={handleSavePost}
                    onCancel={() => setMode('list')}
                />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Long Posts</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreatePost}
                >
                    Create New Post
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    select
                    label="Filter by Category"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    {categories.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            <Grid container spacing={3}>
                {longPosts.map((post) => (
                    <Grid item xs={12} md={6} lg={4} key={post._id}>
                        <LongPostCard 
                            post={post}
                            onCardClick={handleCardClick}
                            onMenuClick={handleMenuClick}
                        />
                    </Grid>
                ))}
            </Grid>

            {longPosts.length === 0 && !loading && (
                <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No long posts found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first long post to get started
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreatePost}
                    >
                        Create Long Post
                    </Button>
                </Box>
            )}

            {/* Menu for post actions */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItemMui onClick={() => { handleEditPost(selectedPost); handleMenuClose(); }}>
                    <Edit sx={{ mr: 1 }} /> Edit Metadata
                </MenuItemMui>
                <MenuItemMui onClick={() => handleDeletePost(selectedPost?._id)}>
                    <Delete sx={{ mr: 1 }} /> Delete Post
                </MenuItemMui>
            </Menu>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Button
                        disabled={page <= 1}
                        onClick={() => handlePageChange(null, page - 1)}
                        sx={{ mr: 2 }}
                    >
                        Previous
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                        Page {page} of {totalPages}
                    </Typography>
                    <Button
                        disabled={page >= totalPages}
                        onClick={() => handlePageChange(null, page + 1)}
                        sx={{ ml: 2 }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </PageContainer>
    );
};

export default LongPostList; 